import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { publicApi } from '../../api/client';
import { useCart } from '../../context/CartContext';
import './Checkout.css';

const Checkout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { clearCart } = useCart();
  const { product, quantity } = location.state || {};

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!product) {
      setSubmitError(t('checkout.noProductError'));
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    const qty = quantity || 1;
    const unitPrice = Number(product.price || 0);
    const subtotal = unitPrice * qty;

    const payload = {
      full_name: formData.fullName,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      items: [
        {
          product_id: product.id,
          product_name: product.name,
          quantity: qty,
          unit_price: unitPrice,
          line_total: subtotal,
        },
      ],
      subtotal,
      promo_code: '',
      discount: 0,
      total: subtotal,
      note: '',
    };

    try {
      await publicApi.post('/api/orders/', payload);
      clearCart();
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(
        error?.response?.data?.detail || t('checkout.submitError')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="checkout-page">
        <div className="container checkout-success">
          <h2>{t('checkout.thankYou')}</h2>
          <p>{t('checkout.thankYouDesc')}</p>
          <Link to="/" className="btn-primary" style={{ display: 'inline-block', marginTop: '24px' }}>{t('checkout.returnHome')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        <h1 className="checkout-title">{t('checkout.title')}</h1>
        
        <div className="checkout-container">
          <div className="checkout-form-section">
            <h2 className="section-title">{t('checkout.shipping')}</h2>
            <form onSubmit={handleSubmit} className="checkout-form">
              <div className="form-group">
                <label>{t('checkout.fullName')}</label>
                <input 
                  type="text" 
                  name="fullName" 
                  value={formData.fullName} 
                  onChange={handleInputChange} 
                  required 
                  placeholder={t('checkout.fullNamePlaceholder')} 
                />
              </div>
              <div className="form-group">
                <label>{t('checkout.phone')}</label>
                <input 
                  type="tel" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleInputChange} 
                  required 
                  placeholder={t('checkout.phonePlaceholder')} 
                />
              </div>
              <div className="form-group">
                <label>{t('checkout.email')}</label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleInputChange} 
                  required 
                  placeholder={t('checkout.emailPlaceholder')} 
                />
              </div>
              <div className="form-group">
                <label>{t('checkout.address')}</label>
                <textarea 
                  name="address" 
                  value={formData.address} 
                  onChange={handleInputChange} 
                  required 
                  placeholder={t('checkout.addressPlaceholder')} 
                  rows="3"
                ></textarea>
              </div>
              
              <button type="submit" className="btn-primary place-order-btn" disabled={isSubmitting}>
                {isSubmitting ? t('checkout.submitting') : t('checkout.placeOrder')}
              </button>
              {submitError && <p style={{ color: '#b91c1c', marginTop: 12 }}>{submitError}</p>}
            </form>
          </div>
          
          <div className="checkout-summary-section">
            <h2 className="section-title">{t('checkout.orderSummary')}</h2>
            {product ? (
              <div className="summary-card">
                <div className="summary-product">
                  <img src={product.imageA || product.image} alt={product.name} className="summary-img" />
                  <div className="summary-info">
                    <h4>{product.name}</h4>
                    <p className="summary-qty">{t('checkout.quantity')}: {quantity || 1}</p>
                    <p className="summary-price">${(product.price * (quantity || 1)).toFixed(2)}</p>
                  </div>
                </div>
                <div className="summary-total">
                  <span>{t('checkout.total')}</span>
                  <span>${(product.price * (quantity || 1)).toFixed(2)}</span>
                </div>
              </div>
            ) : (
              <div className="summary-card empty-summary">
                <p>{t('checkout.noProduct')}</p>
                <Link to="/products" className="btn-secondary">{t('checkout.browseShop')}</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
